import { PERMISSIONS, ROLES } from '@typings/Route';
import update from 'immutability-helper';
import {
    createContext,
    Dispatch,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
} from 'react';
import { useCookies } from 'react-cookie';
import { useGetSessionInfo } from '@apis/nara/Session';

interface RolePayload {
    roleName: Array<ROLES>;
    permissions: Array<PERMISSIONS>;
    userId: string;
    email: string;
}

export enum ActionTypes {
    UPDATE_ROLE,
}

const initialState: { data: RolePayload; isLoading: boolean } = {
    data: {
        roleName: [],
        permissions: [],
        userId: '',
        email: '',
    },
    isLoading: true,
};

const RoleContext = createContext<{
    state: typeof initialState;
    dispatch: Dispatch<{
        type: ActionTypes;
        payload: {
            data: RolePayload;
            isLoading: boolean;
        };
    }>;
}>(null!);

function roleReducer(
    state: typeof initialState,
    action: {
        type: ActionTypes;
        payload: {
            data: RolePayload;
            isLoading: boolean;
        };
    }
): typeof initialState {
    const { payload } = action;
    switch (action.type) {
        case ActionTypes.UPDATE_ROLE: {
            return update(state, {
                data: {
                    roleName: {
                        $set: payload?.data?.roleName,
                    },
                    permissions: { $set: payload?.data?.permissions },
                    userId: { $set: payload.data.userId },
                    email: { $set: payload.data.email },
                },
                isLoading: { $set: payload.isLoading },
            });
        }
        default:
            return state;
    }
}

const RoleDataLoader = ({ dispatch }: { dispatch: Dispatch<any> }) => {
    const [cookies] = useCookies(['accessToken']);
    const accessToken = cookies?.accessToken;

    const { data: dataSession, isError } = useGetSessionInfo({
        accessToken,
    });

    const finishLoading = useCallback(
        (overrides?: Partial<RolePayload>) => {
            dispatch({
                type: ActionTypes.UPDATE_ROLE,
                payload: {
                    data: {
                        roleName: [],
                        permissions: [],
                        userId: '',
                        email: '',
                        ...overrides,
                    },
                    isLoading: false,
                },
            });
        },
        [dispatch]
    );

    const dispatchRole = useCallback(() => {
        if (!accessToken) {
            finishLoading();
            return;
        }
        if (isError) {
            finishLoading();
            return;
        }
        if (!dataSession) return;

        const sessionRoles = dataSession?.accessTokenPayload?.['st-role']?.v ?? [];
        const sessionPermissions =
            dataSession?.accessTokenPayload?.['st-perm']?.v ?? [];

        finishLoading({
            roleName: sessionRoles,
            userId: dataSession?.accessTokenPayload?.sub ?? '',
            email: dataSession?.user?.email ?? '',
            permissions: sessionPermissions,
        });
    }, [accessToken, dataSession, isError, finishLoading]);

    useEffect(() => {
        dispatchRole();
    }, [dispatchRole]);

    return null;
};

const RoleProvider = ({ children }: any) => {
    const [state, dispatch] = useReducer(roleReducer, initialState);
    const value = useMemo(() => ({ state, dispatch }), [state]);
    return (
        <RoleContext.Provider value={value}>
            <RoleDataLoader dispatch={dispatch} />
            {children}
        </RoleContext.Provider>
    );
};

export { RoleContext, RoleProvider };
